import axios from "axios";
import { BASE_URL, AUTH } from "../../../config/axios";

/**
 * ⚠️ IMPORTANT: strips any Content-Type header from AUTH() before sending FormData.
 * If AUTH() hardcodes Content-Type: application/json (common, since most other
 * calls in this app are JSON), that header silently breaks axios's automatic
 * multipart boundary handling — Django then can't parse the uploaded file at all
 * and returns "The submitted data was not a file. Check the encoding type on the form."
 * This is the same class of bug already present in the document upload calls in
 * AddsubmissionData.jsx / AddCommentData.jsx / AddReplyData.jsx — fix those the same way.
 */
function authMultipart() {
  const auth = AUTH();
  if (!auth?.headers) return auth;
  const { "Content-Type": _drop, "content-type": _drop2, ...restHeaders } = auth.headers;
  return { ...auth, headers: restHeaders };
}

/**
 * Fetch a paginated, filtered list of letters for the register.
 * @param {Object} params - { project_details, project, sector, date_from, date_to, search, ordering, page, page_size }
 * (project_details = the overarching project e.g. "Blackspot Porbandar"; project = the section within it)
 */
export async function fetchLetters(params = {}) {
  // Strip empty values so we don't send project_details=&project=&... on every call
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined)
  );

  // NOTE: AUTH() is called as a function — this codebase has a known bug pattern
  // where `AUTH` gets passed as a bare reference instead of `AUTH()`, which sends
  // requests with no Authorization header. Keep the parentheses.
  const res = await axios.get(`${BASE_URL}/dpr/letters/`, {
    ...AUTH(),
    params: cleanParams,
  });
  return res.data; // { count, results: [...] }
}

/**
 * Fetch a single letter with its full correspondence thread.
 * Response is the letter's fields at the top level, plus a `thread` array — not nested under "submission".
 */
export async function fetchLetterDetail(letterId) {
  const res = await axios.get(`${BASE_URL}/dpr/letters/${letterId}/`, AUTH());
  return res.data; // { id, row_id, project, project_details_name, section_name, letter_num, ..., thread: [...] }
}

/**
 * Create a new letter (Submission row) directly in the register.
 * Uses FormData when a file is attached, otherwise a plain object works too — but FormData is
 * safe in both cases, so it's used unconditionally here for simplicity.
 */
export async function createLetter(fields) {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") form.append(key, value);
  });
  const res = await axios.post(`${BASE_URL}/dpr/letters/create/`, form, authMultipart());
  return res.data; // full letter object, including its new row_id
}

/**
 * Create a comment on an existing letter.
 * ⚠️ submissionRowId must be the parent letter's row_id, NOT its id — using id returns a 404.
 */
export async function createLetterComment(submissionRowId, fields) {
  const form = new FormData();
  form.append("submission_row_id", submissionRowId);
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") form.append(key, value);
  });
  const res = await axios.post(`${BASE_URL}/dpr/letters/comment/create/`, form, authMultipart());
  return res.data; // comment object, includes its own row_id (needed to reply to it)
}

/**
 * Create a reply on an existing comment.
 * ⚠️ commentRowId must be the parent comment's row_id, NOT its id.
 */
export async function createLetterReply(commentRowId, fields) {
  const form = new FormData();
  form.append("comment_row_id", commentRowId);
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") form.append(key, value);
  });
  const res = await axios.post(`${BASE_URL}/dpr/letters/reply/create/`, form, authMultipart());
  return res.data; // reply object
}

/**
 * Fetch ProjectDetails (the real "project" a client thinks of, e.g. "Blackspot Porbandar")
 * for the project filter dropdown. Does NOT include sections — see fetchSectionsForProjectDetails.
 * ⚠️ Assuming this endpoint already exists — check with backend dev if this 404s.
 */
export async function fetchProjectDetailsOptions() {
  const res = await axios.get(`${BASE_URL}/dpr/projectdetails/`, AUTH());
  return res.data; // [{ id, project_name, project_short_name, pr_code, sector_details }, ...]
}

/**
 * Fetch the sections under one ProjectDetails.
 * Confirmed correct endpoint (2026-08): /dpr/sectionfilter/<project_details_id>/
 * ⚠️ NOT /dpr/projectfilterbyid/ — that one filters by a SECTION's own id, not a
 * project's id, and will always return empty when passed a project_details id.
 * Returns an array of section objects, each with `id` and `section_name`.
 */
export async function fetchSectionsForProjectDetails(projectDetailsId) {
  if (!projectDetailsId) return [];
  const res = await axios.get(`${BASE_URL}/dpr/sectionfilter/${projectDetailsId}/`, AUTH());
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.data?.results)) return res.data.results;
  console.warn("fetchSectionsForProjectDetails: unexpected response shape", res.data);
  return [];
}

/**
 * Toggle a letter's Open/Closed status.
 * ⚠️ Backend endpoint not built yet — this is scaffolded ahead of time, matching the spec
 * handed to the backend dev. Will 404 until PATCH /dpr/letters/<id>/status/ exists.
 */
export async function updateLetterStatus(letterId, status) {
  const res = await axios.patch(`${BASE_URL}/dpr/letters/${letterId}/status/`, { status }, AUTH());
  return res.data;
}

/**
 * Trigger an Excel export of the currently filtered letters.
 * Backend returns a file blob — kept separate since it needs different response handling.
 * @param {Object} params - same shape as fetchLetters (project_details, project, etc.)
 */
export async function exportLetters(params = {}) {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined)
  );
  const res = await axios.get(`${BASE_URL}/dpr/letters/export/`, {
    ...AUTH(),
    params: cleanParams,
    responseType: "blob",
  });
  return res.data;
}